import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const QuoteRequestsTable = ({ limit }) => {
  const { user } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    if (user?.id) {
      loadQuotes();
    }
  }, [user, filterStatus]);

  const loadQuotes = async () => {
    try {
      setLoading(true);
      let query = supabase?.from('quote_requests')?.select('*')?.eq('provider_id', user?.id)?.order('requested_at', { ascending: false });

      if (filterStatus !== 'all') {
        query = query?.eq('status', filterStatus);
      }

      if (limit) {
        query = query?.limit(limit);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;
      setQuotes(data || []);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (quoteId, newStatus) => {
    try {
      const { error: updateError } = await supabase?.from('quote_requests')?.update({ 
          status: newStatus,
          responded_at: newStatus === 'responded' ? new Date()?.toISOString() : null
        })?.eq('id', quoteId)?.eq('provider_id', user?.id);

      if (updateError) throw updateError;
      loadQuotes();
    } catch (err) {
      console.error('Error updating quote:', err);
    }
  };

  const getUrgencyBadge = (urgency) => {
    const styles = {
      urgent: 'bg-error/10 text-error border-error/20',
      high: 'bg-warning/10 text-warning border-warning/20',
      medium: 'bg-info/10 text-info border-info/20',
      low: 'bg-muted text-muted-foreground border-border',
    };
    return styles?.[urgency] || styles?.low;
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-warning/10 text-warning border-warning/20',
      responded: 'bg-info/10 text-info border-info/20',
      accepted: 'bg-success/10 text-success border-success/20',
      declined: 'bg-muted text-muted-foreground border-border',
      completed: 'bg-primary/10 text-primary border-primary/20',
    };
    return styles?.[status] || styles?.pending;
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('nl-NL', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3]?.map((i) => (
            <div key={i} className="h-16 bg-muted rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <p className="text-error">Fout bij laden: {error}</p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Filters */}
      {!limit && (
        <div className="p-4 border-b border-border">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-muted-foreground">Filter:</span>
            {['all', 'pending', 'responded', 'accepted', 'completed']?.map((status) => (
              <Button
                key={status}
                variant={filterStatus === status ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterStatus(status)}
              >
                {status === 'all' ? 'Alle' : status?.charAt(0)?.toUpperCase() + status?.slice(1)}
              </Button>
            ))}
          </div>
        </div>
      )}
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Klant
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Service Type
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Locatie
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Urgentie
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Datum
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Acties
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {quotes?.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-4 py-8 text-center text-muted-foreground">
                  Geen offerteaanvragen gevonden
                </td>
              </tr>
            ) : (
              quotes?.map((quote) => (
                <tr key={quote?.id} className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-4">
                    <div>
                      <p className="text-sm font-medium text-foreground">{quote?.customer_name}</p>
                      <p className="text-xs text-muted-foreground">{quote?.customer_email}</p>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm text-foreground">{quote?.service_type}</td>
                  <td className="px-4 py-4">
                    <div className="flex items-center space-x-1 text-sm text-foreground">
                      <Icon name="MapPin" size={14} className="text-muted-foreground" />
                      <span>{quote?.location}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">{quote?.postal_code}</p>
                  </td>
                  <td className="px-4 py-4">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                        getUrgencyBadge(quote?.urgency)
                      }`}
                    >
                      {quote?.urgency}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                        getStatusBadge(quote?.status)
                      }`}
                    >
                      {quote?.status}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm text-muted-foreground">
                    {formatDate(quote?.requested_at)}
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center space-x-2">
                      {quote?.status === 'pending' && (
                        <>
                          <Button
                            size="sm"
                            variant="success"
                            onClick={() => handleStatusUpdate(quote?.id, 'responded')}
                          >
                            Reageren
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleStatusUpdate(quote?.id, 'declined')}
                          >
                            Afwijzen
                          </Button>
                        </>
                      )}
                      {quote?.status === 'responded' && (
                        <Button
                          size="sm"
                          variant="success"
                          onClick={() => handleStatusUpdate(quote?.id, 'accepted')}
                        >
                          Accepteren
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default QuoteRequestsTable;